type LazyComponentLoader = () => Promise<unknown>

// 模块级标记:整个会话只预取一次(登录首跳 / F5 重建路由都会经过 afterEach,重复调用直接跳过)
let started = false

// 递归收集动态路由树里所有懒加载组件函数(component 本来就是 () => import(...) 形态)。
// 天然跟随账号权限:权限生成的菜单里有什么页面就预取什么,零维护。
const collectLazyLoaders = (
  routes: AppRouteRecordRaw[],
  seen: Set<LazyComponentLoader>
): LazyComponentLoader[] => {
  const out: LazyComponentLoader[] = []
  for (const route of routes) {
    const component = route.component as unknown
    if (typeof component === 'function' && !seen.has(component as LazyComponentLoader)) {
      seen.add(component as LazyComponentLoader)
      out.push(component as LazyComponentLoader)
    }
    if (route.children?.length) {
      out.push(...collectLazyLoaders(route.children as AppRouteRecordRaw[], seen))
    }
  }
  return out
}

// 空闲调度:requestIdleCallback(5s 超时兜底),不支持的环境退化为 setTimeout 3s
const runIdle = (task: () => void): void => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => task(), { timeout: 5000 })
  } else {
    window.setTimeout(task, 3000)
  }
}

/**
 * 菜单页面闲时预取:趁浏览器空闲把动态路由的懒加载 JS chunk 逐个拉进模块缓存,
 * 点没去过的菜单不再现下载 chunk,达到百毫秒级切入。
 * - 并发 3 条链式推进(每个 import 完成才拉下一个),不与当前页接口抢带宽
 * - 每个 import 失败静默忽略(发版后旧 chunk 404 属正常;真正打开页面时由 router.onError 自动刷新兜底)
 */
export const schedulePrefetchRoutes = (routes: AppRouteRecordRaw[]): void => {
  if (started) return
  started = true

  runIdle(() => {
    const loaders = collectLazyLoaders(routes, new Set())
    let cursor = 0
    const next = (): void => {
      if (cursor >= loaders.length) return
      const loader = loaders[cursor++]
      Promise.resolve()
        .then(() => loader())
        .catch(() => undefined)
        .then(() => next())
    }
    const concurrency = Math.min(3, loaders.length)
    for (let i = 0; i < concurrency; i++) {
      next()
    }
  })
}
